import { type ChangeEvent, type FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import {
  createJobRequestSchema,
  DEFAULT_WRITE_MODEL,
  JOB_REQUEST_MAX,
  JOB_TASK_COUNT_MAX,
  Language,
  LANGUAGES,
  WRITE_MODELS,
  WriteModel,
} from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { useListSourcesQuery } from '../../source/sourceEndpoints';
import { formatFileSize } from '../formatFileSize';
import { useCreateJobMutation, useCreateMaterialUploadMutation } from '../jobEndpoints';
import { MaterialUploadError, StudyMaterialUpload, uploadMaterial } from '../uploadMaterial';

/** The two methods the chooser offers; photos stay hidden until milestone 7 (PDG-D9). */
type JobMethod = 'prompt' | 'pdf';

/** The shape a zod issue needs for `mapIssues`; `ZodIssue` satisfies this. */
interface Issue {
  path: readonly PropertyKey[];
  message: string;
}

/** `createJobRequestSchema`'s top-level keys, mapped to the form's field ids (QZ-D48, PDG-D9). */
const FIELD_IDS: Record<string, string> = {
  language: 'job-language',
  taskCount: 'job-task-count',
  request: 'job-request',
  writeModel: 'job-write-model',
  instructions: 'job-instructions',
  materialKey: 'job-material',
  materialName: 'job-material',
  material: 'job-material',
};

/** Maps the schema's issues to the form's field ids, keyed by `issue.path[0]` (QZ-D48). */
const mapIssues = (issues: readonly Issue[]): Record<string, string[]> => {
  const fields: Record<string, string[]> = {};
  for (const issue of issues) {
    const id = FIELD_IDS[String(issue.path[0])];
    if (!id) {
      continue;
    }
    const list = fields[id] ?? [];
    if (!list.includes(issue.message)) {
      fields[id] = [...list, issue.message];
    }
  }
  return fields;
};

/** A field's issues, shown under it, as `TaskForm`'s `FieldError` does. */
const FieldError = ({ id, messages }: { id: string; messages?: string[] }) => {
  if (!messages || messages.length === 0) {
    return null;
  }
  return (
    <div id={`${id}-error`} className="invalid-feedback d-block">
      {messages.map((message, index) => (
        <div key={index}>{message}</div>
      ))}
    </div>
  );
};

/**
 * The `/jobs/new` form (QZ-D48, PDG-D9): a method chooser, a job language (QZ-D22, default
 * `sv`), a task count and the model that writes the tasks, plus either a request (prompt,
 * QZ-D48) or a PDF study material uploaded straight to S3 (PDF, QZ-D68, QZ-D69). Follows
 * `job-new.html`'s *Prompt* and *PDF* states; the photo state comes in milestone 7.
 */
export const NewJobPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: sourcesData } = useListSourcesQuery();
  const [createJob, { isLoading }] = useCreateJobMutation();
  const [createMaterialUpload] = useCreateMaterialUploadMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [method, setMethod] = useState<JobMethod>('prompt');
  const [language, setLanguage] = useState<Language>('sv');
  const [taskCount, setTaskCount] = useState('40');
  const [request, setRequest] = useState('');
  const [writeModel, setWriteModel] = useState<WriteModel>(DEFAULT_WRITE_MODEL);
  const [material, setMaterial] = useState<StudyMaterialUpload | undefined>();
  const [uploading, setUploading] = useState<{ name: string; sizeBytes: number } | undefined>();
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [instructions, setInstructions] = useState('');
  const [fields, setFields] = useState<Record<string, string[]>>({});
  const [submitError, setSubmitError] = useState<string | undefined>();
  // Bumped at the start of each pick so a stale pick's completion can be told apart from the
  // latest one (PDG-D9).
  const pickIdRef = useRef(0);

  const sourcesLoaded = sourcesData !== undefined;
  const sources = sourcesData?.sources ?? [];
  const noSources = sourcesLoaded && sources.length === 0;
  const materialErrors = [...(fields['job-material'] ?? []), ...(uploadError ? [uploadError] : [])];
  const materialHasError = materialErrors.length > 0;
  // Either the pending pick or the finished one; both share `name` and `sizeBytes` (PDG-D9).
  const materialDisplay = uploading ?? material;
  const materialInputClassName = [
    'form-control',
    materialDisplay ? 'd-none' : undefined,
    materialHasError ? 'is-invalid' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  // PDG-D9: picking a file uploads it at once; a failed Replace leaves no file, so Start
  // cannot send the file the admin meant to replace.
  const handleFilePick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset first so picking the same file again fires `onChange`; this empties the FileList
    // in real browsers, so `file` is captured above before the reset.
    e.target.value = '';
    if (!file) {
      return;
    }

    // PDG-D9: the label stays clickable while a pick uploads, so a later pick can finish first
    // or out of order; only the pick that is still the latest one may touch the state below.
    const pickId = ++pickIdRef.current;

    setUploadError(undefined);
    setMaterial(undefined);
    setUploading({ name: file.name, sizeBytes: file.size });
    try {
      const uploaded = await uploadMaterial(file, (uploadRequest) =>
        createMaterialUpload(uploadRequest).unwrap()
      );
      if (pickIdRef.current === pickId) {
        setMaterial(uploaded);
      }
    } catch (thrown) {
      if (pickIdRef.current === pickId) {
        const code = thrown instanceof MaterialUploadError ? thrown.code : 'unknown';
        setUploadError(t(`error.${code}`));
      }
    } finally {
      if (pickIdRef.current === pickId) {
        setUploading(undefined);
      }
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFields({});
    setSubmitError(undefined);

    let body: Record<string, unknown>;
    if (method === 'prompt') {
      body = {
        request: request.trim(),
        language,
        taskCount: Number(taskCount),
        writeModel,
      };
    } else {
      // Start is disabled until a material is uploaded (PDG-D9), so this only guards a race.
      if (material === undefined) {
        return;
      }
      body = {
        method: 'pdf',
        materialKey: material.key,
        materialName: material.name,
        language,
        taskCount: Number(taskCount),
        writeModel,
      };
      const trimmedInstructions = instructions.trim();
      if (trimmedInstructions) {
        body.instructions = trimmedInstructions;
      }
    }

    const parsed = createJobRequestSchema.safeParse(body);

    if (!parsed.success) {
      setFields(mapIssues(parsed.error.issues));
      return;
    }

    try {
      const job = await createJob(parsed.data).unwrap();
      navigate(`/jobs/${job.jobId}`);
    } catch (thrown) {
      const code = problemCode(thrown);
      setSubmitError(t(code ? `error.${code}` : 'error.unknown'));
    }
  };

  return (
    <main className="qz-main">
      <div className="qz-container is-narrow">
        <Link className="qz-back" to="/jobs">
          <Icon name="arrow-left" /> {t('job.page.back')}
        </Link>

        <div className="qz-page-header">
          <div>
            <h1>{t('job.new.heading')}</h1>
            <p className="qz-sub">{t('job.new.sub')}</p>
          </div>
        </div>

        {/* noValidate: the min/max on task count are a UX hint, not a gate — zod owns validation. */}
        <form className="qz-card" onSubmit={handleSubmit} noValidate>
          <div className="qz-card-body qz-stack">
            <div>
              <div className="qz-label mb-2">{t('job.method.heading')}</div>
              <div className="qz-radio-cards is-two">
                <div className="qz-radio-card">
                  <input
                    type="radio"
                    name="method"
                    id="job-method-prompt"
                    checked={method === 'prompt'}
                    onChange={() => setMethod('prompt')}
                  />
                  <label htmlFor="job-method-prompt">
                    <Icon name="message" />
                    <strong>{t('job.method.prompt')}</strong>
                    <span>{t('job.method.promptHint')}</span>
                  </label>
                </div>
                <div className="qz-radio-card">
                  <input
                    type="radio"
                    name="method"
                    id="job-method-pdf"
                    checked={method === 'pdf'}
                    onChange={() => setMethod('pdf')}
                  />
                  <label htmlFor="job-method-pdf">
                    <Icon name="file-pdf" />
                    <strong>{t('job.method.pdf')}</strong>
                    <span>{t('job.method.pdfHint')}</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-sm-4">
                <label htmlFor="job-language" className="form-label">
                  {t('job.field.language')}
                </label>
                <select
                  id="job-language"
                  className={`form-select${fields['job-language'] ? ' is-invalid' : ''}`}
                  aria-describedby={fields['job-language'] ? 'job-language-error' : undefined}
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as Language)}
                >
                  {LANGUAGES.map((code) => (
                    <option key={code} value={code}>
                      {t(`task.language.${code}`)}
                    </option>
                  ))}
                </select>
                <div className="form-text">{t('job.new.languageHelp')}</div>
                <FieldError id="job-language" messages={fields['job-language']} />
              </div>
              <div className="col-sm-4">
                <label htmlFor="job-task-count" className="form-label">
                  {t('job.field.taskCount')}
                </label>
                <input
                  id="job-task-count"
                  type="number"
                  min={1}
                  max={JOB_TASK_COUNT_MAX}
                  step={1}
                  className={`form-control${fields['job-task-count'] ? ' is-invalid' : ''}`}
                  aria-describedby={fields['job-task-count'] ? 'job-task-count-error' : undefined}
                  value={taskCount}
                  onChange={(e) => setTaskCount(e.target.value)}
                />
                <div className="form-text">{t('job.new.taskCountHelp')}</div>
                <FieldError id="job-task-count" messages={fields['job-task-count']} />
              </div>
              <div className="col-sm-4">
                <label htmlFor="job-write-model" className="form-label">
                  {t('job.field.writeModel')}
                </label>
                <select
                  id="job-write-model"
                  className={`form-select${fields['job-write-model'] ? ' is-invalid' : ''}`}
                  aria-describedby={fields['job-write-model'] ? 'job-write-model-error' : undefined}
                  value={writeModel}
                  onChange={(e) => setWriteModel(e.target.value as WriteModel)}
                >
                  {WRITE_MODELS.map((model) => (
                    <option key={model} value={model}>
                      {t(`job.writeModel.${model}`)}
                    </option>
                  ))}
                </select>
                <div className="form-text">{t('job.new.writeModelHelp')}</div>
                <FieldError id="job-write-model" messages={fields['job-write-model']} />
              </div>
            </div>

            {method === 'prompt' && (
              <div>
                <label htmlFor="job-request" className="form-label">
                  {t('job.field.request')}
                </label>
                <textarea
                  id="job-request"
                  rows={4}
                  maxLength={JOB_REQUEST_MAX}
                  className={`form-control${fields['job-request'] ? ' is-invalid' : ''}`}
                  aria-describedby={fields['job-request'] ? 'job-request-error' : undefined}
                  value={request}
                  onChange={(e) => setRequest(e.target.value)}
                />
                <div className="form-text">{t('job.new.requestHelp')}</div>
                <FieldError id="job-request" messages={fields['job-request']} />

                <div className="qz-small d-flex align-items-center gap-2 flex-wrap mt-2">
                  {noSources ? (
                    <span className="text-danger">{t('error.NoSources')}</span>
                  ) : (
                    <>
                      <span className="qz-muted">{t('job.new.researchesOn')}</span>
                      {sourcesLoaded &&
                        sources.map((source) => (
                          <span key={source.domain} className="qz-tag">
                            {source.domain}
                          </span>
                        ))}
                    </>
                  )}
                  <Link to="/sources">{t('job.new.editSources')}</Link>
                </div>
              </div>
            )}

            {method === 'pdf' && (
              <>
                <div>
                  <label htmlFor="job-material" className="form-label">
                    {t('job.field.material')}
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="job-material"
                    accept="application/pdf,.pdf"
                    className={materialInputClassName}
                    aria-describedby={materialHasError ? 'job-material-error' : undefined}
                    onChange={handleFilePick}
                  />
                  {materialDisplay && (
                    <div className="qz-file">
                      <Icon name="file-pdf" />
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{materialDisplay.name}</div>
                        <div className="qz-small qz-muted">
                          {formatFileSize(materialDisplay.sizeBytes, i18n.language)}
                        </div>
                      </div>
                      {uploading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                          {t('job.new.uploading')}
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {t('job.new.replace')}
                        </button>
                      )}
                    </div>
                  )}
                  <div className="form-text">{t('job.new.materialHelp')}</div>
                  <FieldError id="job-material" messages={materialErrors} />
                </div>

                <div>
                  <label htmlFor="job-instructions" className="form-label">
                    {t('job.field.instructions')}
                  </label>
                  <textarea
                    id="job-instructions"
                    rows={2}
                    maxLength={JOB_REQUEST_MAX}
                    className={`form-control${fields['job-instructions'] ? ' is-invalid' : ''}`}
                    aria-describedby={
                      fields['job-instructions'] ? 'job-instructions-error' : undefined
                    }
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                  />
                  <div className="form-text">{t('job.new.instructionsHelp')}</div>
                  <FieldError id="job-instructions" messages={fields['job-instructions']} />
                </div>
              </>
            )}

            {submitError && (
              <div className="alert alert-danger">
                <Icon name="circle-exclamation" />
                <div>{submitError}</div>
              </div>
            )}
          </div>

          <div className="qz-card-footer d-flex align-items-center gap-2">
            <span className="qz-small qz-muted">
              <Icon name="circle-info" /> {t('job.new.costNote')}
            </span>
            <Link to="/jobs" className="btn btn-outline-secondary ms-auto">
              {t('job.new.cancel')}
            </Link>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                isLoading ||
                (method === 'prompt'
                  ? noSources
                  : uploading !== undefined || material === undefined)
              }
            >
              <Icon name="wand-magic-sparkles" /> {t('job.new.start')}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
};

export default NewJobPage;
